import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SelectionService } from 'shared/domain';

import {
  FlowchartActionDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkAttributeDto,
  FlowchartLinkDto,
  FlowchartNodeType,
  FlowchartNodeDto,
  FlowchartSelectionBehaviorDto,
  FlowchartStartNodeDto,
  FlowchartTextBlockDto,
} from '@treetop/shared/model';

import { FlowchartSidebarStoreService } from './flowchart-sidebar-store.service';
import { FlowchartElementsSubjectStore } from '../../../../../shared/feature-flowchart/src/lib/infrastructure/data/flowchart-elements-subject-store.service';

export type AllFlowchartTypes = FlowchartStartNodeDto & FlowchartActionDto & FlowchartDecisionNodeDto;

/**
 * @Deprecated
 * The LegacySidebarService has been deprecated in favour of a more generic service
 */
@Injectable()
export class LegacySidebarService {
  private selectionService = inject(SelectionService);
  private flowchartSidebarStoreService = inject(FlowchartSidebarStoreService);

  public isSidebarOpen = new BehaviorSubject(true);

  public searchResult = null;
  protected previousDescriptionLength = null;

  public updateCurrentElement(flowchartId: string, id: string, flowchartElements: FlowchartNodeType[], isSubflow?: boolean) {
    this.searchResult = this.deepSearchElements(flowchartElements as FlowchartNodeDto[], id);

    if (isSubflow && (!this.searchResult.children || this.searchResult.children.length === 0)) {
      (this.searchResult as FlowchartActionDto).children = [];
    }

    this.selectionService.selectFlowchartElement(flowchartId, this.searchResult.uniqueId);
    this.flowchartSidebarStoreService.setDto(this.selectionService.selection());
  }

  public updateCurrentElementForLink(flowchartId: string, linkId: string) {
    this.selectionService.selectFlowchartElement(flowchartId, linkId);
  }

  public updateFlowchart(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    dto: Partial<
      FlowchartStartNodeDto &
        FlowchartActionDto &
        FlowchartDecisionNodeDto &
        FlowchartSelectionBehaviorDto &
        FlowchartLinkDto &
        FlowchartTextBlockDto
    >,
    editTitleCallback,
    setLinkAttributeCallback?,
    editTextBoxCallback?,
    setDescriptionCallback?,
    removeDescriptionCallback?,
    editSelectionBehaviorCallback?,
    editColorPresetCallback?,
  ): void {
    if (dto.elementType === FlowchartElementType.FlowchartStartNode) {
      let title = '';
      if (dto.guidelineAction) {
        title = title.concat(dto.guidelineAction.title);
      }
      if (dto.guidelineCriteria) {
        if (title) {
          title = title.concat('|');
        }
        title = title.concat(dto.guidelineCriteria.title);
      }

      editTitleCallback(title, dto.id);
      editColorPresetCallback(dto.colorPresetId, dto.id);
    } else if (
      (dto.elementType === FlowchartElementType.FlowchartAction || dto.elementType === FlowchartElementType.FlowchartSubflow) &&
      !!dto.guidelineAction
    ) {
      editTitleCallback(dto.guidelineAction.title, dto.id);
      editColorPresetCallback(dto.colorPresetId, dto.id);
    } else if (dto.elementType === FlowchartElementType.FlowchartTextBlock) {
      editTextBoxCallback(dto.content, dto.customStyling, dto.id);
      if (dto.colorPresetId) {
        editColorPresetCallback(dto.colorPresetId, dto.id);
      }
    } else if (dto.elementType === FlowchartElementType.FlowchartDecisionNode) {
      if (!!dto.guidelineCriteria || !!dto.guidelineObservation) {
        const guidelineComponent = dto.guidelineCriteria ? dto.guidelineCriteria : dto.guidelineObservation;
        editTitleCallback(guidelineComponent.title, dto.id);
      } else if (dto.ruleTitle) {
        // Decision node based on rule
        editTitleCallback(dto.ruleTitle, dto.id);
      }

      editColorPresetCallback(dto.colorPresetId, dto.id);

      const onlyTruePaths = this.decisionNodeHasOnlyTruePaths(dto.outgoingLinks);

      dto.outgoingLinks?.forEach((link) => {
        if (!onlyTruePaths) {
          setLinkAttributeCallback(link.attribute, link.id, dto.guidelineObservation?.observationDefinition.unit?.name);
        } else {
          setLinkAttributeCallback(null, link.id, undefined);
        }
      });
    }

    if (dto.elementType === FlowchartElementType.FlowchartContainer) {
      editTitleCallback(dto.guidelineAction.title, dto.id);
      const selectionBehavior = dto?.guidelineAction?.selectionBehaviorForChildren;

      editSelectionBehaviorCallback(selectionBehavior, dto.id);

      editColorPresetCallback(dto.colorPresetId, dto.id);
    }

    if (dto.showDescription) {
      const description =
        dto?.guidelineAction?.description ||
        dto?.guidelineCriteria?.description ||
        dto?.guidelineObservation?.description ||
        dto?.ruleDescription;
      removeDescriptionCallback?.(dto.id);
      setDescriptionCallback?.(dto.id, description);

      this.previousDescriptionLength = description?.length;
    } else if (dto.elementType !== FlowchartElementType.FlowchartContainer) {
      removeDescriptionCallback?.(dto.id);
    }

    const updatedFlowchartElements = flowchartElementsSubjectStore.getCloneDeepValue().map((element) => {
      if (element.id === dto.id) {
        return dto;
      }

      if (dto.elementType !== FlowchartElementType.FlowchartLink && element.children && element.children.length) {
        return this.updateChildren(element, dto);
      }

      const updatedOutgoingLinks = element.outgoingLinks?.map((link) => {
        return link.id === dto.id ? dto : link;
      });

      if (element.children && element.children.length) {
        element.children = element.children.map((child) => {
          const updatedChildOutgoingLinks = child.outgoingLinks?.map((link) => (link.id === dto.id ? dto : link));
          return { ...child, outgoingLinks: updatedChildOutgoingLinks };
        });
      }

      return { ...element, outgoingLinks: updatedOutgoingLinks };
    });

    flowchartElementsSubjectStore.next(updatedFlowchartElements);
  }

  private updateChildren(element, dto) {
    if (element.children && element.children.length) {
      const updatedChildren = element.children.map((child) => {
        if (child.id === dto.id) {
          return dto;
        }
        return this.updateChildren(child, dto);
      });
      return { ...element, children: updatedChildren };
    }
    return element;
  }

  public decisionNodeHasOnlyTruePaths(links: FlowchartLinkDto[]): boolean {
    return links?.every((link) => link.attribute?.value?.booleanValue === true || link?.attribute === null);
  }

  public isAttributeValueSet(attribute: FlowchartLinkAttributeDto) {
    if (attribute) {
      const entries = Object.entries(attribute?.value).filter(([_, value]) => {
        return this.isValueSet(value);
      });
      return entries.length >= 1;
    }

    return false;
  }

  public isValueSet(value) {
    if (value instanceof Object) {
      return Object.keys(value).length !== 0;
    } else {
      return value !== null && value !== undefined && value !== '';
    }
  }

  public openSidebar(): void {
    this.isSidebarOpen.next(true);
  }

  public closeSidebar(): void {
    this.isSidebarOpen.next(false);
  }

  public toggleSidebar(): void {
    this.isSidebarOpen.next(!this.isSidebarOpen.getValue());
  }

  public getUnitFromSourceLinkDecision(id: string, elements: AllFlowchartTypes[]) {
    const el = this.deepSearchElements(elements as FlowchartDecisionNodeDto[], id) as FlowchartDecisionNodeDto;
    const unit = el?.guidelineObservation?.unit?.name
      ? el?.guidelineObservation?.unit?.name
      : el?.guidelineObservation?.observationDefinition?.unit?.name;
    if (unit) {
      return unit;
    } else {
      return null;
    }
  }

  public deepSearchElements(elements: FlowchartNodeDto[], id: string): FlowchartNodeDto {
    let result: FlowchartNodeDto;
    elements.forEach((el) => {
      if (el.id === id) {
        result = el;
      } else if (el.children) {
        const child = this.deepSearchElements(el.children, id);
        if (child) {
          result = child;
        }
      }
    });
    return result;
  }

  public deepSearchLinks(elements, id: string) {
    if (!elements) {
      return null;
    }

    for (let i = 0; i < elements.length; i++) {
      const result = elements[i].outgoingLinks?.find((link) => link.id === id);

      if (result) {
        return result;
      }

      const recursiveResult = this.deepSearchLinks(elements[i].children, id);

      if (recursiveResult) {
        return recursiveResult;
      }
    }

    return null;
  }
}
